import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Input from './Input';
import { useAuth } from '../../contexts/AuthContext';
import { laseaUserService } from '../../services/laseaUserService';
import { supabase } from '../../lib/supabase';

const AdminClientSelector = ({ isOpen = false, onClose, onClientSelect, refreshTrigger = 0 }) => {
  const { userRole } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [activeTab, setActiveTab] = useState('clients'); // 'clients' or 'movers'
  const [clients, setClients] = useState([]);
  const [movers, setMovers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();

    const storedSelectedClient = localStorage.getItem('selectedClient');
    if (storedSelectedClient && storedSelectedClient !== 'undefined') {
      try {
        setSelectedClient(JSON.parse(storedSelectedClient));
      } catch (e) {
        console.error('Failed to parse selectedClient from localStorage', e);
        localStorage.removeItem('selectedClient');
      }
    }
  }, [refreshTrigger]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch Clients
      const customers = (await laseaUserService?.getAllCustomers()) || [];

      // Fetch all active item counts in a single SQL query instead of N+1 PostgREST count calls
      const WORKER_URL = import.meta.env.VITE_WORKER_URL;
      const WORKER_SECRET = import.meta.env.VITE_WORKER_SECRET;
      let countsByClientId = {};
      try {
        const res = await fetch(`${WORKER_URL}/data/query`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Worker-Auth': WORKER_SECRET },
          body: JSON.stringify({
            query: `SELECT client_id, COUNT(*) as count FROM lasea_inventory WHERE is_active = true GROUP BY client_id`,
            params: []
          })
        });
        const rows = await res.json();
        if (Array.isArray(rows)) {
          rows.forEach(row => { countsByClientId[row.client_id] = parseInt(row.count, 10); });
        }
      } catch (err) {
        console.error('Error fetching inventory counts:', err);
      }

      const clientsWithCounts = customers.map((customer) => ({
        id: customer?.id,
        name: customer?.fullName || 'N/A',
        image: customer?.clientImage || null,
        company: customer?.companyName || 'N/A',
        email: customer?.email,
        activeItems: countsByClientId[customer?.id] || 0,
        lastActivity: new Date(customer.updatedAt || customer.createdAt)?.toISOString()?.split('T')?.[0],
        status: 'active'
      }));
      setClients(clientsWithCounts || []);

      // Fetch Movers
      const moversData = (await laseaUserService?.getAllMovers()) || [];
      const formattedMovers = moversData.map(mover => ({
        id: mover?.id,
        name: mover?.fullName || 'N/A',
        image: mover?.clientImage || null,
        company: 'LaSea Team', // Movers belong to the team
        email: mover?.email,
        activeItems: 0, // Movers don't own items
        lastActivity: new Date(mover.updatedAt || mover.createdAt)?.toISOString()?.split('T')?.[0],
        status: 'active'
      }));
      setMovers(formattedMovers || []);

    } catch (error) {
      console.error('Error fetching data:', error);
      setClients([]);
      setMovers([]);
    } finally {
      setLoading(false);
    }
  };

  const currentList = activeTab === 'clients' ? clients : movers;

  const filteredList = currentList?.filter(item =>
    item?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    item?.company?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    item?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    localStorage.setItem('selectedClient', JSON.stringify(client));
    if (onClientSelect) {
      onClientSelect(client);
    }
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success/10 text-success border-success/20';
      case 'pending':
        return 'bg-warning/10 text-warning border-warning/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-foreground/20 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      <aside
        className={`fixed lg:sticky top-16 left-0 h-[calc(100vh-4rem)] w-80 bg-card border-r border-border z-50 transition-page overflow-hidden flex flex-col ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}
      >
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between mb-4 lg:hidden">
            <h2 className="text-lg font-semibold text-foreground">Select {activeTab === 'clients' ? 'Client' : 'Mover'}</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-md hover:bg-muted transition-smooth"
              aria-label="Close sidebar"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <div className="flex p-1 bg-muted rounded-lg mb-4">
            <button
              onClick={() => setActiveTab('clients')}
              className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${activeTab === 'clients'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
                }`}
            >
              Clients
            </button>
            {userRole === 'admin' && (
              <button
                onClick={() => setActiveTab('movers')}
                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${activeTab === 'movers'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
                  }`}
              >
                Movers
              </button>
            )}
          </div>

          <Input
            type="search"
            placeholder={`Search ${activeTab}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="mb-4"
          />

          {selectedClient && (
            <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex items-center gap-3 mb-2">
                <Icon name="CheckCircle2" size={18} color="var(--color-primary)" />
                <span className="text-sm font-medium text-primary">Selected Client</span>
              </div>
              <p className="text-sm font-semibold text-foreground ml-9">{selectedClient?.name}</p>
              <p className="text-xs text-muted-foreground ml-9">{selectedClient?.company}</p>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3"></div>
              <p className="text-sm text-muted-foreground">Loading clients...</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredList?.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">
                    {currentList?.length === 0
                      ? `No ${activeTab} registered yet`
                      : `No ${activeTab} found`}
                  </p>
                </div>
              ) : (
                filteredList?.map((client) => (
                  <button
                    key={client?.id}
                    onClick={() => handleClientSelect(client)}
                    className={`w-full p-4 rounded-lg border transition-smooth text-left touch-target ${selectedClient?.id === client?.id
                      ? 'bg-primary/10 border-primary' : 'bg-background border-border hover:bg-muted'
                      }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 flex gap-3">
                        {client.image ? (
                          <img
                            src={client.image}
                            alt={client.name}
                            className="w-10 h-10 rounded-full object-cover border border-border"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Icon name="User" size={20} color="var(--color-primary)" />
                          </div>
                        )}
                        <div>
                          <h3 className="text-sm font-semibold text-foreground mb-1">
                            {client?.name}
                          </h3>
                          <p className="text-xs text-muted-foreground mb-2">{client?.company}</p>
                          <p className="text-xs text-muted-foreground">{client?.email}</p>
                        </div>
                      </div>
                      {selectedClient?.id === client?.id && (
                        <Icon name="Check" size={20} color="var(--color-primary)" />
                      )}
                    </div>

                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                      <div className="flex items-center gap-2">
                        <Icon name="Package" size={14} color="var(--color-muted-foreground)" />
                        <span className="text-xs text-muted-foreground">{client?.activeItems} items</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(client?.status)}`}>
                        {client?.status}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

      </aside>
    </>
  );
};

export default AdminClientSelector;